import { Component } from '@theme/component';

/**
 * Filters treatment cards by category, client-side.
 *
 * Each card carries a `data-categories` attribute — a comma-separated list of the
 * category indices it belongs to. A card may belong to several, so it can appear
 * under more than one tab.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} grid - The card grid.
 * @property {HTMLButtonElement[]} [tabButtons] - The category pills.
 *
 * @extends Component<Refs>
 */
class TreatmentGrid extends Component {
  requiredRefs = ['grid'];

  connectedCallback() {
    super.connectedCallback();
    this.#applyActiveFilter();
  }

  /**
   * Re-runs after a Section Rendering API morph, which can replace this
   * component's markup underneath it and reset the cards to their unfiltered state.
   */
  updatedCallback() {
    super.updatedCallback();
    this.#applyActiveFilter();
  }

  /** @returns {HTMLButtonElement[]} */
  get #tabs() {
    return this.refs.tabButtons ?? [];
  }

  /** @returns {HTMLElement[]} */
  get #cards() {
    return /** @type {HTMLElement[]} */ ([...this.refs.grid.querySelectorAll('[data-categories]')]);
  }

  /**
   * Handles a click on a category pill.
   *
   * @param {PointerEvent} event - The click event.
   */
  selectTab(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const tab = target.closest('[data-category]');
    if (!(tab instanceof HTMLElement)) return;

    const category = tab.dataset.category;
    if (!category) return;

    for (const button of this.#tabs) {
      button.setAttribute('aria-pressed', String(button === tab));
    }

    this.#filter(category);
  }

  /**
   * Applies whichever tab is currently marked active. The active tab is rendered
   * server-side, so this also establishes the initial state on first connect.
   */
  #applyActiveFilter() {
    const tabs = this.#tabs;
    if (tabs.length === 0) return;

    const active = tabs.find((tab) => tab.getAttribute('aria-pressed') === 'true') ?? tabs[0];
    const category = active?.dataset.category;

    if (category) this.#filter(category);
  }

  /**
   * Shows only the cards belonging to a category.
   *
   * A card with no categories set is hidden from every tab — a half-configured
   * card fails quietly rather than appearing everywhere.
   *
   * @param {string} category - The category index to show.
   */
  #filter(category) {
    for (const card of this.#cards) {
      const values = (card.dataset.categories ?? '')
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean);

      card.hidden = !values.includes(category);
    }
  }
}

if (!customElements.get('treatment-grid')) {
  customElements.define('treatment-grid', TreatmentGrid);
}
